/**
 * Pure layout transforms for the plan-view drawing mode.
 *
 * These are the *edits* a drag performs — move a run along its wall, pull one
 * of its ends, slide the heater — expressed as pure `InteriorLayout ->
 * InteriorLayout` functions. Deliberately NOT rules:
 *
 *  - Whether a proposed layout FITS is `reconcileInteriorLayout`'s job.
 *  - Whether a proposed layout is LEGAL is `validateInteriorLayout`'s job.
 *
 * A transform here is free to return a layout that is off the wall or through
 * the heater; the drag pipeline reconciles and validates it before anything is
 * shown as valid or committed. That split is what keeps the plan view from
 * growing a second copy of the clearance rules — the failure this module would
 * otherwise invite (see the mirrored-constants note).
 */
import { MIN_BENCH_RUN_LENGTH_MM } from "./constants";
import type {
	BenchRun,
	HeaterPlacement,
	InteriorLayout,
	InteriorWall,
} from "./types";

/**
 * Drag granularity, millimetres.
 *
 * A builder measures to the centimetre and cuts to the millimetre; free-float
 * pointer positions would persist values like 743.28 mm into the design payload
 * and churn every snapshot for a precision no sawmill has. 10 mm is also fine
 * enough that the snap is invisible at plan-view scale.
 */
export const PLAN_SNAP_STEP_MM = 10;

/** Round a millimetre value to the drag grid. */
export function snapMm(
	valueMm: number,
	stepMm: number = PLAN_SNAP_STEP_MM,
): number {
	if (!Number.isFinite(valueMm)) return 0;
	if (stepMm <= 0) return valueMm;
	return Math.round(valueMm / stepMm) * stepMm;
}

/** Which end of a run a resize handle pulls. */
export type BenchRunEdge = "start" | "end";

/**
 * Shortest run the plan view will let a resize produce.
 *
 * Mirrors nothing: `MIN_BENCH_RUN_LENGTH_MM` in `constants.ts` is the length
 * below which reconciliation DROPS a run entirely, and a drag that silently
 * deletes the thing you were resizing is a trap. Resizing therefore stops at
 * that floor instead of crossing it, so the run always survives the gesture.
 */
export const PLAN_MIN_RUN_LENGTH_MM = MIN_BENCH_RUN_LENGTH_MM;

function replaceRun(
	layout: InteriorLayout,
	runId: string,
	next: (run: BenchRun) => BenchRun,
): InteriorLayout {
	let changed = false;
	const benchRuns = layout.benchRuns.map((run) => {
		if (run.id !== runId) return run;
		const updated = next(run);
		if (updated !== run) changed = true;
		return updated;
	});
	return changed ? { ...layout, benchRuns } : layout;
}

/**
 * Slide a run along its own wall. `startMm` is measured from the wall's
 * low-coordinate corner, exactly as `BenchRun.startMm` is stored.
 */
export function moveBenchRunAlongWall(
	layout: InteriorLayout,
	runId: string,
	startMm: number,
): InteriorLayout {
	return replaceRun(layout, runId, (run) =>
		run.startMm === startMm ? run : { ...run, startMm },
	);
}

/**
 * Pull one END of a run to a position along the wall, keeping the other end
 * planted. Returns the run unchanged when the drag would take it below the
 * minimum usable length — the opposite end stays put, so the gesture reads as
 * "it stopped", not "it jumped".
 */
export function resizeBenchRun(
	layout: InteriorLayout,
	runId: string,
	edge: BenchRunEdge,
	positionMm: number,
): InteriorLayout {
	return replaceRun(layout, runId, (run) => {
		const fixed = edge === "start" ? run.startMm + run.lengthMm : run.startMm;
		const startMm = edge === "start" ? Math.min(positionMm, fixed) : fixed;
		const lengthMm =
			edge === "start" ? fixed - startMm : Math.max(0, positionMm - fixed);

		if (lengthMm < MIN_BENCH_RUN_LENGTH_MM) return run;
		if (startMm === run.startMm && lengthMm === run.lengthMm) return run;
		return { ...run, startMm, lengthMm };
	});
}

/** Change a run's seat depth, measured out from its wall. */
export function setBenchRunDepth(
	layout: InteriorLayout,
	runId: string,
	depthMm: number,
): InteriorLayout {
	return replaceRun(layout, runId, (run) =>
		run.depthMm === depthMm ? run : { ...run, depthMm },
	);
}

/**
 * Slide the heater along the wall it already stands on. `offsetMm` is the
 * unit's CENTRE, measured from that wall's low-coordinate corner.
 */
export function moveHeaterAlongWall(
	layout: InteriorLayout,
	offsetMm: number,
): InteriorLayout {
	if (layout.heater.offsetMm === offsetMm) return layout;
	return { ...layout, heater: { ...layout.heater, offsetMm } };
}

/**
 * Stand the heater against a different wall.
 *
 * The gap to the wall is carried over rather than reset: a user who pulled the
 * unit 200 mm off the back wall means "200 mm off the wall", not "200 mm off
 * the back wall specifically". Its footprint re-orients on its own —
 * `heaterFootprint` resolves the size against the placement's own wall.
 */
export function moveHeaterToWall(
	layout: InteriorLayout,
	wall: InteriorWall,
	offsetMm: number,
): InteriorLayout {
	if (layout.heater.wall === wall && layout.heater.offsetMm === offsetMm) {
		return layout;
	}
	return { ...layout, heater: { ...layout.heater, wall, offsetMm } };
}

/**
 * Structural equality for two layouts.
 *
 * The drag pipeline commits through `setInteriorLayout`, and the store's zundo
 * history records EVERY set — there is no equality guard and no debounce. A
 * gesture that ends where it began would therefore burn an undo step that
 * restores nothing, so the commit is skipped when this returns true.
 */
/**
 * Every field of a `BenchRun` that the equality check must look at.
 *
 * Written out as a `Record<keyof BenchRun, true>` on purpose: adding a field to
 * `BenchRun` without adding it here is a TYPE ERROR, not a silently weakened
 * guard. The previous hand-written comparison covered 9 of 13 fields, so a new
 * field would have made "did this drag change anything?" answer no and the
 * change would have been dropped on commit with the suite still green.
 */
const BENCH_RUN_COMPARED_FIELDS: Record<keyof BenchRun, true> = {
	id: true,
	room: true,
	wall: true,
	tier: true,
	startMm: true,
	lengthMm: true,
	depthMm: true,
	offsetFromWallMm: true,
	legHeightMm: true,
	seatHeightMm: true,
	supportMode: true,
	woodSpecies: true,
	slatGapMm: true,
	heightGroupId: true,
	skirtEnabled: true,
	backrestEnabled: true,
	backrestStartMm: true,
	backrestLengthMm: true,
	backrestAboveSeatMm: true,
	backrestBoardCount: true,
};

const BENCH_RUN_FIELDS = Object.keys(
	BENCH_RUN_COMPARED_FIELDS,
) as (keyof BenchRun)[];

/** Same treatment for the heater placement. */
const HEATER_COMPARED_FIELDS: Record<keyof HeaterPlacement, true> = {
	wall: true,
	offsetMm: true,
	wallGapMm: true,
};

const HEATER_FIELDS = Object.keys(
	HEATER_COMPARED_FIELDS,
) as (keyof HeaterPlacement)[];

export function interiorLayoutsEqual(
	a: InteriorLayout | null,
	b: InteriorLayout | null,
): boolean {
	if (a === b) return true;
	if (!a || !b) return false;
	if (a.variant !== b.variant) return false;
	if (HEATER_FIELDS.some((field) => a.heater[field] !== b.heater[field])) {
		return false;
	}
	if (a.benchRuns.length !== b.benchRuns.length) return false;
	return a.benchRuns.every((run, index) => {
		const other = b.benchRuns[index];
		return BENCH_RUN_FIELDS.every((field) => run[field] === other[field]);
	});
}
